import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { PinInputWrapper } from './PinInput.style';

export const PinInputItem: FunctionComponent<IPinInputItemProps> = ({
  handleKeyDown,
  handleChange,
  handleClick,
  setRef,
  name
}) => {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) {
      setRef(ref.current);
    }
  }, [ref]);
  return (
    <input
      ref={ref}
      onKeyDown={handleKeyDown}
      onChange={handleChange}
      onClick={handleClick}
      maxLength={1}
      name={name}
    />
  );
};

const PinInput: FunctionComponent<IPinInputProps> = ({ initialValue, isError, onChange }) => {
  const [refs, setRefs] = useState<HTMLInputElement[]>([]);
  const [inputs] = useState<{ index: number }[]>(() => [...Array(8)].map((_, index) => ({ index })));
  const BACKSPACE_KEY = 'Backspace';

  useEffect(() => {
    if (initialValue === '' && refs.length === 8) {
      refs.forEach(ref => {
        ref.value = '';
      });
    }
  }, [initialValue]);

  const updateInputList = (inputElements: HTMLInputElement[]) => {
    let pinResult: string = '';
    if (inputElements.length > 0) {
      pinResult = inputElements.map(el => el.value.toUpperCase()).join('');
    }
    onChange(pinResult);
  };

  const setRef = (index: number) => (ref: HTMLInputElement) => {
    refs[index] = ref;
    setRefs(refs);
    if (initialValue && initialValue[index]) {
      ref.value = initialValue[index];
    }
  };

  const handleKeyDown = (index: number) => (event: React.KeyboardEvent<HTMLInputElement>) => {
    const { key, currentTarget } = event;
    if (!key.match(/[a-zA-Z0-9]/)) {
      event.preventDefault();
    }
    if (key === BACKSPACE_KEY) {
      if (currentTarget.value) {
        currentTarget.value = '';
        return;
      }
      const prevRef = refs[index - 1];
      if (!prevRef) return;
      prevRef.value = '';
      prevRef.focus();
      return;
    }
  };

  const handleInputChange = (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    if (!value) return;
    updateInputList(refs);

    const nextRef = refs[index + 1];
    if (nextRef) {
      nextRef.focus();
      nextRef.select();
      return;
    }
  };

  const handleInputClick = (event: React.MouseEvent<HTMLInputElement>) => {
    const { currentTarget } = event;
    if (!currentTarget.value) return;
    currentTarget.focus();
    currentTarget.select();
  };

  return (
    <PinInputWrapper data-testid="pinInput" isError={isError}>
      {inputs.map(({ index }) => (
        <PinInputItem
          key={index}
          handleKeyDown={handleKeyDown(index)}
          handleChange={handleInputChange(index)}
          setRef={setRef(index)}
          handleClick={handleInputClick}
          refs={refs}
          name={`pinInput-${index}`}
        />
      ))}
    </PinInputWrapper>
  );
};

export default PinInput;
